// frontend/src/utils/localStorageHandler.ts
import { IfcClassesEnum, ZViewerIfcClass } from "@multiconsult-group/multiplacer-core"; // juster sti
import { IfcClassesToDisplay, IMcBimItemColorMode } from "./designSlice";

const KEYS = {
  ifcClassesToDisplay: "mcbim.ifcClassesToDisplay",
  mcbimItemColorMode: "mcbim.itemColorMode",
} as const;

export const DEFAULT_IFC_CLASSES_TO_DISPLAY: IfcClassesToDisplay = [{ ifcClass: IfcClassesEnum.IFCFURNITURE, opacity: 1 }];

const DEFAULT_MCBIM_ITEM_COLOR_MODE: IMcBimItemColorMode = "SYSTEM_AND_CLASS";

const safeJsonParse = <T>(raw: string | null): T | null => {
  if (raw == null) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const safeJsonStringify = (value: unknown): string | null => {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
};

export const loadIfcClassesToDisplay = (): IfcClassesToDisplay => {
  const stored = safeJsonParse<IfcClassesToDisplay>(
    localStorage.getItem(KEYS.ifcClassesToDisplay)
  );
  const parsed = ZViewerIfcClass.array().safeParse(stored);
  return parsed.success ? parsed.data : DEFAULT_IFC_CLASSES_TO_DISPLAY.map(entry => ({ ...entry }));
};

export const saveIfcClassesToDisplay = (value: IfcClassesToDisplay) => {
  const json = safeJsonStringify(value);
  if (json == null) return;
  try {
    localStorage.setItem(KEYS.ifcClassesToDisplay, json);
  } catch {
    // ignore
  }
};

// --------------------
// MCBIM item color mode
// --------------------

const isValidMcBimItemColorMode = (v: unknown): v is IMcBimItemColorMode => {
  return v === "SYSTEM_AND_CLASS" || v === "MULTIPLACER_COLORS" || v === "MMI" || v === "DISCIPLINE_COLORS" || v === "PRIMARY_SYSTEM";
};

export const loadMcBimItemColorMode = (): IMcBimItemColorMode => {
  const raw = localStorage.getItem(KEYS.mcbimItemColorMode);
  if (!raw) return DEFAULT_MCBIM_ITEM_COLOR_MODE;

  const parsed = safeJsonParse<unknown>(raw);
  if (isValidMcBimItemColorMode(parsed)) return parsed;

  // Backward compatibility if you ever stored as plain string without JSON
  if (isValidMcBimItemColorMode(raw)) return raw;

  return DEFAULT_MCBIM_ITEM_COLOR_MODE;
};

export const saveMcBimItemColorMode = (value: IMcBimItemColorMode) => {
  const json = safeJsonStringify(value);
  if (json == null) return;
  try {
    localStorage.setItem(KEYS.mcbimItemColorMode, json);
  } catch {
    // ignore
  }
};

// Norsk (Bokmål): Lagring for filter-modus (INSTANCE_CLASS / DISCIPLINE)
export const saveMcBimItemsFilterMode = (mode: "INSTANCE_CLASS" | "DISCIPLINE") => {
  localStorage.setItem("mcbimItemsFilterMode", mode);
};

export const loadMcBimItemsFilterMode = (): "INSTANCE_CLASS" | "DISCIPLINE" => {
  return (localStorage.getItem("mcbimItemsFilterMode") as any) || "INSTANCE_CLASS";
};

// Norsk (Bokmål): Lagring for valgte disipliner
const DISCIPLINES_TO_DISPLAY_KEY = "disciplinesToDisplay";

export const saveDisciplinesToDisplay = (map: Partial<Record<string, boolean>>) => {
  localStorage.setItem(DISCIPLINES_TO_DISPLAY_KEY, JSON.stringify(map ?? {}));
};

export const loadDisciplinesToDisplay = (): Partial<Record<string, boolean>> => {
  try {
    const raw = localStorage.getItem(DISCIPLINES_TO_DISPLAY_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
};
